import api from './api';
import { socketService } from './socketService';
import { authService } from './authService';

export interface Message {
  id: number;
  conversation_id: number;
  sender_id: number;
  content: string;
  attachment_url?: string;
  video_url?: string;
  link_url?: string;
  message_type?: string;
  is_read: boolean;
  created_at: string;
  sender: {
    id: number;
    username: string;
    full_name?: string;
    avatar?: string;
    role?: string;
  };
}

export interface Conversation {
  id: number;
  user1_id: number;
  user2_id: number;
  other_user: {
    id: number;
    username: string;
    full_name?: string;
    avatar?: string;
    is_online?: boolean;
    last_seen?: string;
    is_team?: boolean;
  };
  last_message?: Message;
  last_message_at?: string;
  unread_count: number;
  created_at: string;
}

export interface MessagesResponse {
  messages: Message[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export const supportChatService = {
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get<Conversation[]>('/support-chat/conversations');
    return response.data;
  },

  createOrGetConversation: async (): Promise<Conversation> => {
    const response = await api.post<Conversation>('/support-chat/conversations');
    return response.data;
  },

  getMessages: async (conversationId: number, page: number = 1, perPage: number = 50): Promise<MessagesResponse> => {
    const response = await api.get<MessagesResponse>(`/support-chat/conversations/${conversationId}/messages`, {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  sendMessage: async (
    conversationId: number, 
    content: string, 
    options?: { 
      attachment?: File; 
      video?: File; 
      linkUrl?: string;
    }
  ): Promise<Message> => {
    if (options?.attachment || options?.video) {
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('content', content);
      if (options.attachment) {
        formData.append('attachment', options.attachment);
      }
      if (options.video) {
        formData.append('video', options.video);
      }
      if (options.linkUrl) {
        formData.append('link_url', options.linkUrl);
      }
      
      const response = await api.post<Message>(`/support-chat/conversations/${conversationId}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } else {
      // Use JSON for text-only or link messages
      const response = await api.post<Message>(`/support-chat/conversations/${conversationId}/messages`, { 
        content,
        link_url: options?.linkUrl
      });
      return response.data;
    }
  },

  markMessageRead: async (messageId: number): Promise<Message> => {
    const response = await api.put<Message>(`/support-chat/messages/${messageId}/read`);
    return response.data;
  },

  markAllRead: async (conversationId: number): Promise<void> => {
    await api.put(`/support-chat/conversations/${conversationId}/read-all`);
  },

  // Socket methods
  joinConversation: (conversationId: number) => {
    const token = authService.getToken();
    if (!token) {
      console.error('❌ No token available for joining conversation');
      return;
    }
    
    if (!socketService.isConnected()) {
      console.warn('⚠️ Socket not connected, cannot join conversation');
      socketService.connect();
      setTimeout(() => {
        if (socketService.isConnected()) {
          socketService.socket?.emit('join_conversation', {
            token,
            conversation_id: conversationId,
          });
        }
      }, 500);
      return;
    }
    
    socketService.socket?.emit('join_conversation', {
      token,
      conversation_id: conversationId,
    });
    
    socketService.socket?.once('joined_conversation', (data: any) => {
      // Confirmed joined conversation
    });
  },

  leaveConversation: (conversationId: number) => {
    const token = authService.getToken();
    if (token && socketService.isConnected()) {
      socketService.socket?.emit('leave_conversation', {
        token,
        conversation_id: conversationId,
      });
    }
  },

  onNewMessage: (callback: (message: Message) => void) => {
    return socketService.on('new_message', callback);
  },

  onConversationUpdate: (callback: (conversation: Conversation) => void) => {
    return socketService.on('conversation_update', callback);
  },

  onUserStatusUpdate: (callback: (data: { user_id: number; is_online: boolean; last_seen?: string }) => void) => {
    return socketService.on('user_status_update', callback);
  },

  sendHeartbeat: () => {
    const token = authService.getToken();
    if (token && socketService.isConnected()) {
      socketService.socket?.emit('heartbeat', { token });
    }
  },
};

